/**
 * Stamp the protagonist onto a compiled-episode player payload so the vendored
 * Cocos player buckets portraits by role: protagonist → LEFT, everyone else →
 * RIGHT.
 *
 * The player decides a character's side purely from `protagonistName` — it reads
 * NO per-step position/side field. It resolves `protagonistName` from the loaded
 * payload:
 *   - `characters[]` (mapped to `game.currentCharacters`): the entry flagged
 *     `isProtagonist` / `role: "MC"` supplies `protagonistName` via its `slug`;
 *   - `currentNovel.roleplayCharacterName` (mapped to `game.currentNovel`): a
 *     scalar fallback used only when no roster entry qualifies.
 *
 * A compiled episode carries NEITHER — so without this the player sees
 * `protagonistName = null` and piles every sprite on one side. We add both seams
 * (belt-and-suspenders) so either path resolves the MC.
 *
 * Side is decided by a slug-match: the player lowercases both the on-stage
 * character token and `protagonistName` and strips every non-`[a-z0-9]` char
 * before comparing, so passing the compiler-form slug (`malia`) matches sprites
 * authored as `MALIA` / `Malia` / `Ma-lia`.
 *
 * No-op when `slug` is empty, when `payload` is not a plain object (e.g. a raw
 * episodes array), or when it already carries a non-empty `characters` roster
 * (trust an upstream one rather than clobber it).
 */
export function withProtagonist(payload, slug) {
    if (!slug || typeof payload !== "object" || payload === null || Array.isArray(payload)) {
        return payload;
    }
    const obj = payload;
    if (Array.isArray(obj.characters) && obj.characters.length > 0) {
        return payload;
    }
    const currentNovel = obj.currentNovel && typeof obj.currentNovel === "object" && !Array.isArray(obj.currentNovel)
        ? obj.currentNovel
        : {};
    return {
        ...obj,
        characters: [{ slug, isProtagonist: true, role: "MC" }],
        currentNovel: { ...currentNovel, roleplayCharacterName: slug }
    };
}
